<template>
    <TransitionModal>
        <Fixed
            v-if="!!stoneId"
            :fetching="fetching"
            class="stone-modal"
            data-qa="stone-modal"
            @close="close"
        >
            <ModalWithHeaderAndFooter
                :title-element-id
                @close="close"
            >
                <template
                    v-if="title"
                    #title
                >
                    <h3
                        :id="titleElementId"
                        class="stone-title"
                    >
                        {{ title }}
                    </h3>
                </template>

                <template #default>
                    <LoadingAnimation
                        v-if="fetching"
                        class="loading-animation"
                    />

                    <div v-else>
                        <ErrorFiveHundredMessage
                            v-if="error"
                            :errors="errors"
                        />

                        <StoneDetails
                            v-else-if="stone"
                            :data-qa-stone-modal-props="
                                JSON.stringify({
                                    shape: stone.shapeWithInfo.code,
                                    weight: stone.weight.toFixed(2),
                                    color: stone.colorWithInfo.code,
                                    symmetry: stone.symmetryWithInfo?.code,
                                    table: stone.tablePercent,
                                    girdle: `${stone.girdleMin}-${stone.girdleMax}`,
                                    cut: stone.cutWithInfo?.code,
                                    clarity: stone.clarityWithInfo.code,
                                    fluorescence:
                                        stone.fluorescenceWithInfo?.code,
                                    depth: stone.depth,
                                    polish: stone.polishWithInfo?.code,
                                    ratio: stone.ratio,
                                })
                            "
                            :image
                            :show-photo-text="true"
                            :title
                            :v360-url="stone.v360Url"
                        >
                            <dl
                                v-if="isGemstone"
                                class="details"
                            >
                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.price') }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/gemstones/price"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{ price }}
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.property_cut') }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/gemstones/cut"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.cutWithInfo?.info?.translation
                                                ?.name ||
                                            stone.cutWithInfo?.code
                                        }}
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.property_carat_weight'
                                            )
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/gemstones/carat-weight"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{ Number(stone.weight).toFixed(2) }}ct
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.gemstone_type') }}
                                    </dt>

                                    <dd class="description gemstone">
                                        {{
                                            stone.colorWithInfo.info
                                                ?.translation.name ||
                                            stone.colorWithInfo.code
                                        }}
                                        <span class="crystal">{{
                                            stone.crystal
                                        }}</span>
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.gemstone_colour_grade'
                                            )
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/gemstones/colour-grade"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.gradeWithInfo?.info
                                                ?.translation.name ||
                                            stone.gradeWithInfo?.code
                                        }}
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.property_clarity')
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/gemstones/clarity"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.clarityWithInfo.info
                                                ?.translation.name ||
                                            stone.clarityWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="
                                        stone.length &&
                                        stone.width &&
                                        stone.depth
                                    "
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.measurements') }}
                                    </dt>

                                    <dd class="description">
                                        {{ stone.length }} x {{ stone.width }} x
                                        {{ stone.depth }}
                                    </dd>
                                </div>
                            </dl>

                            <dl
                                v-else
                                class="details"
                            >
                                <div class="details-item">
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.price') }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/price"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{ price }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.cutWithInfo"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.property_cut') }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/cut"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.cutWithInfo.info?.translation
                                                .name || stone.cutWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.weight"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.property_carat_weight'
                                            )
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/carat-weight"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{ stone.weight }}
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        v-if="stone.colorWithInfo"
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.property_colour')
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/colour"
                                        />
                                    </dt>

                                    <dd
                                        v-if="stone.colorWithInfo"
                                        class="description"
                                    >
                                        {{
                                            stone.colorWithInfo.info
                                                ?.translation.name ||
                                            stone.colorWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.clarityWithInfo"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.property_clarity')
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/clarity"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.clarityWithInfo.info
                                                ?.translation.name ||
                                            stone.clarityWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.polishWithInfo"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.property_polish')
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/polish"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.polishWithInfo.info
                                                ?.translation.name ||
                                            stone.polishWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div class="details-item">
                                    <dt
                                        v-if="stone.symmetryWithInfo"
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.property_symmetry'
                                            )
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/symmetry"
                                        />
                                    </dt>

                                    <dd
                                        v-if="stone.symmetryWithInfo"
                                        class="description"
                                    >
                                        {{
                                            stone.symmetryWithInfo.info
                                                ?.translation.name ||
                                            stone.symmetryWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.fluorescenceWithInfo"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.property_fluorescence'
                                            )
                                        }}
                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/fluorescence"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{
                                            stone.fluorescenceWithInfo.info
                                                ?.translation.name ||
                                            stone.fluorescenceWithInfo.code
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="
                                        stone.length &&
                                        stone.width &&
                                        stone.depth
                                    "
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.measurements') }}
                                    </dt>

                                    <dd class="description">
                                        {{ stone.length }} x {{ stone.width }} x
                                        {{ stone.depth }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.tablePercent"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.property_table_percent'
                                            )
                                        }}
                                    </dt>

                                    <dd class="description">
                                        {{ stone.tablePercent.toFixed(2) }}%
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.depthPercent"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t(
                                                'diamond_search.property_depth_percent'
                                            )
                                        }}
                                    </dt>

                                    <dd class="description">
                                        {{ stone.depthPercent.toFixed(2) }}%
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.ratio"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        Ratio
                                    </dt>

                                    <dd class="description">
                                        {{ stone.ratio }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.girdleMin && stone.girdleMax"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.property_girdle')
                                        }}
                                    </dt>

                                    <dd class="description">
                                        {{ systemCodeToWords(stone.girdleMin) }}
                                        -
                                        {{ systemCodeToWords(stone.girdleMax) }}
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.originCountry"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.source_of_origin')
                                        }}

                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/country-of-origin"
                                        />
                                    </dt>

                                    <dd class="description">
                                        <div
                                            class="country-with-flag"
                                            data-allow-mismatch="text"
                                        >
                                            <Flag
                                                :country="stone.originCountry"
                                            />

                                            {{
                                                getCountryName(
                                                    stone.originCountry
                                                )
                                            }}
                                        </div>
                                    </dd>
                                </div>

                                <div
                                    v-else-if="certificate"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{
                                            t('diamond_search.source_of_origin')
                                        }}

                                        <LazyTooltip
                                            :page-type="pageType"
                                            path="/diamond-search/natural-diamonds/kimberley-process"
                                        />
                                    </dt>
                                    <dd class="description">
                                        {{
                                            t(
                                                'diamond_search.kimberley_process'
                                            )
                                        }}
                                    </dd>
                                </div>

                                <div
                                    v-if="originReport"
                                    class="details-item"
                                >
                                    <dl
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('diamond_search.origin_report') }}
                                    </dl>
                                    <dd class="description">
                                        <a
                                            :aria-label="`${originReport.label} - opens in a new tab`"
                                            :href="originReport.url"
                                            rel="nofollow"
                                            target="_blank"
                                        >
                                            {{ originReport.label }}
                                        </a>
                                    </dd>
                                </div>

                                <div
                                    v-if="certificate"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ certificate.lab }}
                                        {{ t('diamond_search.report') }}

                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/gia-report"
                                        />
                                    </dt>

                                    <dd class="description">
                                        <a
                                            :aria-label="`${certificate.number} - opens in a new tab`"
                                            :href="certificate.url"
                                            class="link"
                                            rel="nofollow"
                                            target="_blank"
                                        >
                                            {{ certificate.number }}
                                        </a>
                                    </dd>
                                </div>

                                <div
                                    v-if="stone.isCanadaMark"
                                    class="details-item"
                                >
                                    <dt
                                        aria-level="4"
                                        class="title"
                                        role="heading"
                                    >
                                        {{ t('products.brand') }}

                                        <LazyTooltip
                                            :page-type="pageType"
                                            inline
                                            path="/diamond-search/natural-diamonds/canada-mark"
                                        />
                                    </dt>

                                    <dd class="description">
                                        {{ t('products.canada_mark') }}
                                    </dd>
                                </div>
                            </dl>

                            <ShopWithConfidence
                                :ethically-sourced-diamonds="!isGemstone"
                                :ethically-sourced-gemstones="isGemstone"
                                :ninety-day-returns
                                :page-type="pageType"
                                price-match-guarantee
                            />
                        </StoneDetails>

                        <ErrorFourHundredFourMessage v-else>
                            <template #title>
                                {{ t('diamond_search.stone_not_found_title') }}
                            </template>

                            <template #message>
                                <p>
                                    {{
                                        t(
                                            'diamond_search.stone_not_found_message'
                                        )
                                    }}
                                </p>
                            </template>
                        </ErrorFourHundredFourMessage>
                    </div>

                    <div
                        v-if="
                            stoneAlreadyAdded &&
                            (!ringBuilderRing || viewCompletedRingRoute)
                        "
                        v-qa="'stone-added'"
                        class="stone-added-wrapper"
                    >
                        {{ t('diamond_search.stone_already_added') }}
                    </div>

                    <div
                        v-if="stone && isAdmin"
                        v-qa="'stone-consultant-view'"
                        class="stone-consultant-view"
                    >
                        <div class="stone-consultant-view-wrapper">
                            <div class="consultant-view-title">
                                Consultant view
                            </div>
                            <div class="consultant-view-items">
                                <div class="consultant-view-item">
                                    <span class="consultant-view-item-title">
                                        Identifier:
                                    </span>
                                    <span class="consultant-view-item-value">
                                        {{ stone.identifier.toUpperCase() }}
                                    </span>
                                </div>

                                <div class="consultant-view-item">
                                    <span class="consultant-view-item-title">
                                        Black inclusion:
                                    </span>
                                    <span
                                        v-if="stone.blackInclusion"
                                        class="consultant-view-item-value"
                                    >
                                        {{ stone.blackInclusion }}
                                    </span>
                                    <span
                                        v-else
                                        class="consultant-view-item-value"
                                    >
                                        N/A
                                    </span>
                                </div>

                                <div class="consultant-view-item">
                                    <span class="consultant-view-item-title">
                                        Delivery:
                                    </span>
                                    <span
                                        v-if="stone.deliveryDays"
                                        class="consultant-view-item-value"
                                    >
                                        Up to {{ stone.deliveryDays }} days
                                    </span>
                                    <span
                                        v-else
                                        class="consultant-view-item-value"
                                    >
                                        N/A
                                    </span>
                                </div>

                                <div class="consultant-view-item">
                                    <span class="consultant-view-item-title">
                                        Shipping From:
                                    </span>
                                    <span
                                        v-if="stone.shippingFromCountry"
                                        class="consultant-view-item-value"
                                    >
                                        <Flag
                                            :country="stone.shippingFromCountry"
                                        />
                                        {{
                                            getCountryName(
                                                stone.shippingFromCountry
                                            )
                                        }}
                                    </span>
                                    <span
                                        v-else
                                        class="consultant-view-item-value"
                                    >
                                        N/A
                                    </span>
                                </div>

                                <div class="consultant-view-item">
                                    <span class="consultant-view-item-title">
                                        Cost Price:
                                    </span>
                                    <span
                                        v-if="stone.price"
                                        class="consultant-view-item-value"
                                    >
                                        USD {{ stone.price }}
                                        pre-vat
                                    </span>
                                    <span
                                        v-else
                                        class="consultant-view-item-value"
                                    >
                                        N/A
                                    </span>
                                </div>
                            </div>

                            <div class="consultant-view-stone-link">
                                <input
                                    ref="consultantLink"
                                    :value="document?.URL"
                                    class="consultant-view-readonly-input"
                                    readonly
                                    type="text"
                                    @focus="
                                        /** @ts-expect-error alabala */ selectAndCopyToClipboard(
                                            $event.target
                                        )
                                    "
                                />
                                <ThButton
                                    size="small"
                                    type="solid"
                                    @click="
                                        selectAndCopyToClipboard(
                                            $refs[
                                                'consultantLink'
                                            ] as HTMLInputElement
                                        )
                                    "
                                >
                                    <span v-if="consultantLinkCopied">
                                        Copied!
                                    </span>
                                    <span v-else>Copy</span>
                                </ThButton>
                            </div>

                            <div class="consultant-view-button-wrapper">
                                <ThButton
                                    v-qa="'view-in-gems-button'"
                                    :aria-label="'View in gems - opens in a new tab'"
                                    :href="`https://gems.taylorandhart.com/admin/stones/aggregated-by-identifier/${stone.identifier}`"
                                    color="var(--blue)"
                                    size="small"
                                    target="_blank"
                                    type="bordered"
                                >
                                    View in gems
                                </ThButton>
                            </div>
                        </div>
                    </div>
                </template>

                <template
                    v-if="!fetching"
                    #footer
                >
                    <div
                        v-if="stone"
                        class="select-button-wrapper"
                    >
                        <!-- TODO Handle when stone is sold out https://taylorandhart.atlassian.net/browse/FO-1946 -->

                        <span v-if="stoneAlreadyAdded">
                            <ThButton
                                v-if="viewCompletedRingRoute"
                                v-qa="'view-completed-ring'"
                                :to="viewCompletedRingRoute"
                                color="var(--blue)"
                                type="solid"
                            >
                                {{ t('diamond_search.view_completed_ring') }}
                            </ThButton>

                            <ThButton
                                v-else-if="chooseYourRing"
                                v-qa="'choose-your-ring'"
                                :to="chooseYourRing"
                                color="var(--blue)"
                                type="solid"
                            >
                                {{ t('diamond_search.choose_your_ring') }}
                            </ThButton>
                        </span>
                        <span v-else>
                            <ThButton
                                v-if="stone.containsEngagementRings"
                                v-qa="'select-this-stone'"
                                color="var(--blue)"
                                type="solid"
                                @click="promptAddToRingBuilder"
                            >
                                {{ t('diamond_search.select_stone') }}
                            </ThButton>

                            <ThButton
                                v-else
                                v-qa="'select-this-stone'"
                                :loading="existingCustomerFetching"
                                color="var(--blue)"
                                type="solid"
                                @click="handleEnquireCta"
                            >
                                {{ t('diamond_search.enquire_stone') }}
                            </ThButton>
                        </span>
                        <!-- TODO Handle errors on existing customer -->
                    </div>
                </template>
            </ModalWithHeaderAndFooter>
        </Fixed>
    </TransitionModal>

    <AuthFormInModal
        ref="authForm"
        :content
        :is-open="isEnquiryModalOpen"
        :registration-disabled="newCustomerFetching"
        :registration-errors="errors"
        @close="closeEnquiryModal"
        @login="
            (_, type) => {
                if (type === 'manual') handleEnquireCta()
            }
        "
        @register="submitEnquiryNewCustomer"
    >
    </AuthFormInModal>

    <ThDialog
        :confirm-label="'Yes'"
        :is-open="isAddToRingBuilderDialogOpen"
        :prompt="t('diamond_search.change_stone')"
        @close="closeAddToRingBuilderDialog"
        @confirm="confirmAddToRingBuilder"
    />
</template>

<script lang="ts" setup>
import { computed, ref, toRef, useTemplateRef } from 'vue'
import type { EnquiryDiamondSearchProduct, StoneFragment } from '@/graphql/api'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useTranslator } from '@/i18n'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { getUri } from '@/helpers/getCurrentUri'
import {
    createResponseAndPath,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { useCountryPlugin } from '@/plugins/country.ts'
import { maybeStoneIdentifier } from '@/domain/stone'
import { toCertificate } from '@/domain/stone/certificate'
import { useStoneOriginReport } from '@/domain/stone/brand'
import { useStoneImage } from '@/domain/stone/useStoneImage'
import { useStonePrice } from '../useStonePrice'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useRoutes } from '@/router/routes.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useRouter } from 'vue-router'
import { getDocument, getNavigator } from '@/helpers/globals.ts'
import { useAuth } from '@/plugins/auth.ts'
import { useGetStoneQuery } from '@/graphql/api/stone/gems/GetStone.query.generated.ts'
import { useCreateDiamondSearchProductEnquiryExistingCustomerMutation } from '@/graphql/api/product/stones/createDiamondSearchProductEnquiryExistingCustomer.mutation.generated.ts'
import { useCreateDiamondSearchProductEnquiryNewCustomerMutation } from '@/graphql/api/product/stones/createDiamondSearchProductEnquiryNewCustomer.mutation.generated.ts'
import { useRingBuilder } from '@/plugins/ring-builder.ts'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import ModalWithHeaderAndFooter from '@/components/layout/modal/ModalWithHeaderAndFooter.vue'
import Fixed from '@/components/layout/modal/Fixed.vue'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'
import ErrorFiveHundredMessage from '@/components/ui/error/ErrorFiveHundredMessage.vue'
import ErrorFourHundredFourMessage from '@/components/ui/error/ErrorFourHundredFourMessage.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import StoneDetails from '@/components/stone/search/sku/StoneDetails.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'
import ShopWithConfidence from '../ShopWithConfidence.vue'
import Flag from '@/components/Flag.vue'
import ThDialog from '@/components/layout/modal/ThDialog.vue'
import AuthFormInModal from '@/components/user/auth/AuthFormInModal.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'
import { useInit } from '@/plugins/init/use-init.ts'
import { ROUTE_PROFILE_DESIGN_STUDIO } from '@/router'
import { systemCodeToWords } from '@/helpers'

const router = useRouter()
const props = defineProps<{
    stoneId?: string
    isGemstone?: boolean
    pageType: PageType
}>()

const document = getDocument()

const { enquiryFormContent } = useInit()

const consultantLinkCopied = ref(false)

const selectAndCopyToClipboard = (element: HTMLInputElement) => {
    element.select()
    element.setSelectionRange(0, 99999) // For mobile devices

    getNavigator()?.clipboard.writeText(element.value)
    consultantLinkCopied.value = true
}

const emit = defineEmits<{
    close: []
    select: [stone: StoneFragment]
    enquiry: [stone: StoneFragment]
}>()

const close = () => {
    emit('close')
}

const { t } = useI18n()
const currency = useCurrencyInput()
const { countryCode } = useCountryPlugin()
const { locale, getCountryName } = useTranslator()

const identifier = computed(() => maybeStoneIdentifier(props.stoneId))
const response = useGetStoneQuery({
    variables: computed(() => ({
        identifier: identifier.value || '',
        locale: locale.value,
        country: countryCode.value!,
        currency: currency.value,
    })),
    pause: () => !identifier.value,
})

const { fetching, error, data } = response
const stone = toRef(() => data.value?.GemsApi_getStone)

const routes = useRoutes()

const stoneImage = useStoneImage(stone)
const image = computed(
    () =>
        stoneImage.value && {
            ...stoneImage.value,
            alt: `${
                stone.value?.shapeWithInfo.info?.translation.name ||
                stone.value?.shapeWithInfo.code
            } ${
                stone.value?.categoryWithInfo.info?.translation.name ||
                stone.value?.categoryWithInfo.code
            }`,
        }
)
const certificate = toCertificate(stone)

const originReport = useStoneOriginReport(stone)

const { price, totalSaleAmount } = useStonePrice(stone)

const ninetyDayReturns = computed(
    () => parseFloat(totalSaleAmount.value.amount) < 20000
)

const title = computed(() => {
    if (fetching.value) {
        return t('globals.loading')
    }

    const s = stone.value

    if (!s) {
        return ''
    }

    return `${
        s.shapeWithInfo.info?.translation.name || s.shapeWithInfo.code
    } ${s.weight}ct`
})

const content = computed(() => enquiryFormContent.value['stone-modal'])

const {
    addStone,
    stone: ringBuilderStone,
    ring: ringBuilderRing,
} = useRingBuilder()

const viewCompletedRingRoute = computed(() => {
    if (!ringBuilderRing.value) {
        return undefined
    }

    const to = routes.ringBuilder.ring(
        ringBuilderRing.value,
        ringBuilderStone.value
    )

    if (router.currentRoute.value.name === to.name) {
        return undefined
    }
    return to
})

const chooseYourRing = computed(() => {
    if (ringBuilderRing.value || !stoneAlreadyAdded.value) {
        return undefined
    }

    const to = routes.engagementRings({
        shape: stone.value?.shapeWithInfo.info?.systemCode,
    })

    if (router.currentRoute.value.name === to.name) {
        return undefined
    }

    return to
})

const stoneAlreadyAdded = computed(
    () =>
        stone.value &&
        ringBuilderStone.value?.__typename === 'GemsApi_Stone' &&
        ringBuilderStone.value.identifier === stone.value.identifier
)

const isAddToRingBuilderDialogOpen = ref(false)
const openAddToRingBuilderDialog = () => {
    isAddToRingBuilderDialogOpen.value = true
}
const closeAddToRingBuilderDialog = () => {
    isAddToRingBuilderDialogOpen.value = false
}

const promptAddToRingBuilder = () => {
    if (ringBuilderStone.value) {
        openAddToRingBuilderDialog()
    } else {
        selectStone()
    }
}

const confirmAddToRingBuilder = () => {
    selectStone()
    closeAddToRingBuilderDialog()
}

const selectStone = async () => {
    const st = stone.value

    if (st) {
        await addStone(st)

        emit('select', st)
    }
}

const getEnquiryInput = (): EnquiryDiamondSearchProduct | undefined => {
    const st = stone.value

    if (!st) {
        // IMPORTANT! If you add any other `return undefined`
        // Make sure the user can not hit a dead end
        // Because code that uses this function stops without any user-friendly UI interaction
        return undefined
    }

    return {
        commissionJunction: getCommissionJunctionInput(),
        currency: currency.value,
        country: countryCode.value!,
        uri: getUri(),
        gemsApiStoneIdentifier: st.identifier,
    }
}

const { isLogged } = useAuth()

// Enquiry existing customer
const existingCustomerResponse =
    useCreateDiamondSearchProductEnquiryExistingCustomerMutation()
const {
    executeMutation: existingCustomerMutation,
    fetching: existingCustomerFetching,
    // error: existingCustomerError,
} = existingCustomerResponse
// const errorExistingCustomerCollection = useResponseError(
//     existingCustomerResponse
// )

const authForm = useTemplateRef('authForm')

const submitEnquiryExistingCustomer = (input: EnquiryDiamondSearchProduct) => {
    existingCustomerMutation({ input }).then(({ data, error }) => {
        if (data && !error)
            authForm.value?.handleRegistrationResponse(undefined, {
                name: ROUTE_PROFILE_DESIGN_STUDIO,
            })
    })
}

// Enquiry new customer

// Enquiry modal

const isEnquiryModalOpen = ref<boolean>(false)
const closeEnquiryModal = () => {
    isEnquiryModalOpen.value = false
}

const { customerRegistrationInput } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const newCustomerResponse =
    useCreateDiamondSearchProductEnquiryNewCustomerMutation()
const { executeMutation: newCustomerMutation, fetching: newCustomerFetching } =
    newCustomerResponse
// const errorNewCustomerCollection = useResponseError(newCustomerResponse)

const errors = useLastResponseErrorAtPath([
    createResponseAndPath(
        existingCustomerResponse,
        'createEnquiryDiamondSearchProductExistingCustomer'
    ),
    createResponseAndPath(
        newCustomerResponse,
        'createEnquiryDiamondSearchProductNewCustomer'
    ),
])

const isAdmin = useIsAdmin()

const submitEnquiryNewCustomer = (successCallback: () => void) => {
    const input = getEnquiryInput()
    const customer = customerRegistrationInput.value

    if (!input || !customer) {
        return
    }

    newCustomerMutation({
        input,
        customer,
        isConsultant: isAdmin.value,
    }).then(({ data, error }) => {
        if (data && !error) {
            authForm.value?.handleRegistrationResponse?.(
                data.createEnquiryDiamondSearchProductNewCustomer.customer,
                {
                    name: ROUTE_PROFILE_DESIGN_STUDIO,
                }
            )
        }

        if (data?.createEnquiryDiamondSearchProductNewCustomer.customer.login) {
            successCallback()
        }

        closeEnquiryModal() // TODO: Close only if no error ?
    })
}

const handleEnquireCta = () => {
    const input = getEnquiryInput()

    if (!input) {
        return
    }

    if (isLogged.value) {
        submitEnquiryExistingCustomer(input)
    } else {
        isEnquiryModalOpen.value = true
    }
}

const titleElementId = 'stone-modal-title-id'
</script>

<style lang="scss" scoped>
.stone-modal {
    display: flex;
    justify-content: center;
    align-items: center;

    .loading-animation {
        padding-bottom: 50px;
    }

    .details-item .description.gemstone .crystal {
        text-transform: lowercase;
    }
}

.select-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    @include min-width(1024) {
        width: 570px;
        margin: 0 auto;
    }
}

.stone-title {
    text-transform: capitalize;
    font-size: 1.5rem;
    line-height: 1.2em;
    font-family: $parcel-font-stack;
    font-weight: 400;
    color: $blue;
}

.stone-added-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0 0 0;

    &:after {
        content: $icon-checkmark;
        color: $blue;
        font-family: $icon-font;
        margin: 0 0 0 10px;
        font-size: 0.875rem;
    }
}

.stone-consultant-view {
    border: 1px solid $grey-200;
    border-radius: 4px;
    background: $stone-2;
    width: calc(100% - 40px);
    margin: 40px auto 0;
    padding: 40px 20px;

    .stone-consultant-view-wrapper {
        width: 100%;
        max-width: 600px;
        margin: 0 auto;
    }

    .consultant-view-title {
        font-family: $parcel-font-stack;
        font-size: 1.2rem;
        color: $blue;
        font-weight: 500;
        margin: 0 0 40px;

        @include min-width(768) {
            text-align: center;
        }
    }

    .consultant-view-items {
        display: grid;
        grid-template-columns: 1fr;
        grid-auto-rows: auto;
        gap: 20px;
        width: 100%;

        @include min-width(768) {
            margin: 0 auto;
            grid-template-columns: 1fr 1fr;
        }

        .consultant-view-item-title {
            font-family: $effra-font-stack;
            font-size: 14px;
            text-transform: uppercase;
            font-weight: 400;
            line-height: 15px;
            letter-spacing: 0.05em;
        }

        .consultant-view-item-value {
            font-family: $effra-font-stack;
            font-size: 14px;
            text-transform: uppercase;
            font-weight: 400;
            line-height: 15px;
            letter-spacing: 0.05em;
            color: $grey-500;
        }
    }

    .consultant-view-stone-link {
        margin: 20px auto;
        width: 100%;
        display: flex;

        .consultant-view-readonly-input {
            background: $white;
            border: 1px solid $grey-200;
            border-radius: 4px;
            padding: 10px 20px;
            width: 100%;
            margin-right: 20px;
        }
    }

    .consultant-view-button-wrapper {
        @include min-width(768) {
            display: flex;
            justify-content: center;
        }
    }
}
</style>
